import { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectAuth, setActiveDropDown, setActiveDropDownTab, setActiveTab } from "../../redux/features/authSlice";
import TotalInfoCard from "../../components/TotalInfoCard";
import DashboardChartCard from "../../components/DashboardChartCard";
import CustomLoading from "../../components/CustomLoading";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
} from 'chart.js';
import { useGetRiderDashboardQuery } from "../../redux/api/profileApi/profileApi";
import jwt_decode from 'jwt-decode'
import { IAuthUser } from "../../redux/features/authSlice/authSlice.types";
import { BiFilterAlt } from "react-icons/bi";
import { GiReceiveMoney, GiTakeMyMoney } from "react-icons/gi";
import { BsFillBoxSeamFill } from "react-icons/bs";
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
);

export interface ITotalInfo {
    icon: ReactNode;
    value: number;
    title: string;
    iconBgColor: string;
    divBgColor: string;
}


const Dashboard = () => {
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const { token } = useSelector(selectAuth);
    const authData: IAuthUser = token ? jwt_decode(token) : null;
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { data: dashboardData, isLoading: dashboardLoading } = useGetRiderDashboardQuery({
        id: authData?.id,
        from_date: fromDate,
        to_date: toDate,
    });
    const [totalInfo, setTotalInfo] = useState([
        {
            title: "Total Parcel",
            value: 0,
            icon: <BsFillBoxSeamFill />,
            iconBgColor: "#99DD00",
            divBgColor: "#F5FCE5",
        },
        {
            title: "Total Paid Amount",
            value: 0,
            icon: <GiTakeMyMoney />,
            iconBgColor: "#0099DD",
            divBgColor: "#E5F5FC",
        },
        {
            title: "Total Collected Amount",
            value: 0,
            icon: <GiReceiveMoney />,
            iconBgColor: "#F67402",
            divBgColor: "#FEF1E6",
        },
    ]);
    const [pickupChartData, setPickupChartData] = useState({
        labels: [
            "Total Pickup Request",
            "Total Pickup Cancel",
            "Total Pickup Pending",
        ],
        datasets: [
            {
                data: [0, 0, 0],
                backgroundColor: ["#99DD00", "#F67402", "#0099DD"],
            },
        ],
    });
    const [deliveryChartData, setDeliveryChartData] = useState({
        labels: [
            "Total Delivered Parcel",
            "Total Delivery Cancelled",
            "Total Delivery Pending",
        ],
        datasets: [
            {
                data: [120, 80, 110],
                backgroundColor: ["#99DD00", "#F67402", "#0099DD"],
            },
        ],
    });
    const [pickupData, setPickupData] = useState([
        {
            title: "Total Pickup Request",
            value: 0,
            darkBgColor: "#99DD00",
            lightBgColor: "#F5FCE5",
        },
        {
            title: "Total Pickup Cancel",
            value: 0,
            darkBgColor: "#F67402",
            lightBgColor: "#FEF1E6",
        },
        {
            title: "Total Pickup Pending",
            value: 0,
            darkBgColor: "#0099DD",
            lightBgColor: "#E5F5FC",
        },
    ]);
    const [deliveryData, setDeliveryData] = useState([
        {
            title: "Total Delivered Parcel",
            value: 0,
            darkBgColor: "#99DD00",
            lightBgColor: "#F5FCE5",
        },
        {
            title: "Total Delivery Cancel",
            value: 0,
            darkBgColor: "#F67402",
            lightBgColor: "#FEF1E6",
        },
        {
            title: "Total Delivery Pending",
            value: 0,
            darkBgColor: "#0099DD",
            lightBgColor: "#E5F5FC",
        },
    ]);

    useEffect(() => {
        if (dashboardData) {
            const updatedTotalInfo = totalInfo.map(item => {
                if (item.title === 'Total Parcel') {
                    return { ...item, value: dashboardData?.parcel?.length || 0 };
                } else if (item.title === 'Total Paid Amount') {
                    return { ...item, value: dashboardData?.collectAmount || 0 };
                } else if (item.title === 'Total Collected Amount') {
                    return { ...item, value: dashboardData?.collectAmount || 0 };
                }
                return item;
            });
            setTotalInfo(updatedTotalInfo);

            const updatedPickupData = pickupData.map(item => {
                if (item.title === 'Total Pickup Request') {
                    return { ...item, value: dashboardData?.parcel?.filter(item => item?.parcel?.pickup_status === 'Requested')?.length || 0 };
                } else if (item.title === 'Total Pickup Cancel') {
                    return { ...item, value: dashboardData?.parcel?.filter(item => item?.parcel?.pickup_status === 'Cancelled')?.length || 0 };
                } else if (item.title === 'Total Pickup Pending') {
                    return { ...item, value: dashboardData?.parcel?.filter(item => item?.parcel?.pickup_status === 'Pending')?.length || 0 };
                }
                return item;
            });
            setPickupData(updatedPickupData);

            const updatedDeliveryData = deliveryData.map(item => {
                if (item.title === 'Total Delivered Parcel') {
                    return { ...item, value: dashboardData?.parcel?.filter(item => item?.parcel?.delivery_status === 'Delivered')?.length || 0 };
                } else if (item.title === 'Total Delivery Cancel') {
                    return { ...item, value: dashboardData?.parcel?.filter(item => item?.parcel?.delivery_status === 'Cancelled')?.length || 0 };
                } else if (item.title === 'Total Delivery Pending') {
                    return { ...item, value: dashboardData?.parcel?.filter(item => item?.parcel?.delivery_status === 'Pending')?.length || 0 };
                }
                return item;
            });
            setDeliveryData(updatedDeliveryData);

            setPickupChartData({
                ...pickupChartData,
                datasets: [
                    {
                        ...pickupChartData?.datasets[0],
                        data: [dashboardData?.parcel?.filter(item => item?.parcel?.pickup_status === 'Requested')?.length, dashboardData?.parcel?.filter(item => item?.parcel?.pickup_status === 'Cancelled')?.length, dashboardData?.parcel?.filter(item => item?.parcel?.pickup_status === 'Pending')?.length]
                    }
                ]
            });
            setDeliveryChartData({
                ...deliveryChartData,
                datasets: [
                    {
                        ...deliveryChartData?.datasets[0],
                        data: [dashboardData?.parcel?.filter(item => item?.parcel?.delivery_status === 'Delivered')?.length, dashboardData?.parcel?.filter(item => item?.parcel?.delivery_status === 'Cancelled')?.length, dashboardData?.parcel?.filter(item => item?.parcel?.delivery_status === 'Pending')?.length]
                    }
                ]
            });
        }
    }, [dashboardData]);

    useEffect(() => {
        if (pathname === "/") {
            dispatch(setActiveTab("Dashboard"));
            dispatch(setActiveDropDownTab(""));
            dispatch(setActiveDropDown(null))
        }
    }, [dispatch, pathname]);

    return (
        <div className="w-full">
            <div className="w-full bg-white shadow-lg rounded-lg flex flex-col lg:flex-row">
                <div className="w-full xl:w-[60%] p-4">
                    <div className="text-3xl font-bold text-text_1 mb-2">
                        Total Informations
                    </div>
                    <div className="text-text_2 text-xl mb-8">All dashboard</div>
                    <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                        {totalInfo?.map((info, index) => {
                            return (
                                <TotalInfoCard
                                    key={index}
                                    title={info?.title}
                                    value={info?.value}
                                    icon={info?.icon}
                                    iconBgColor={info?.iconBgColor}
                                    divBgColor={info?.divBgColor}
                                />
                            );
                        })}
                    </div>
                </div>
                <div className="w-full xl:w-[40%]">
                    <img
                        src="https://res.cloudinary.com/di23omfvx/image/upload/v1692428968/self_driving_car_Road_1_npjom3.gif"
                        alt="self drive"
                        className="h-full w-full"
                    />
                </div>
            </div>
            <div className='w-full flex flex-col lg:flex-row items-end gap-4 bg-white shadow-lg my-8 p-4 rounded-lg'>
                <div className='w-full'>
                    <div className='text-text_1 text-lg font-bold mb-2'>From Date</div>
                    <input type="date" id="start" name="trip-start" value={fromDate} onChange={e => setFromDate(e.target.value)} className='w-full border-2 p-2 rounded-lg focus:border-secondary focus:outline-none' />
                </div>
                <div className='w-full'>
                    <div className='text-text_1 text-lg font-bold mb-2'>To Date</div>
                    <input type="date" id="start" name="trip-start" value={toDate} onChange={e => setToDate(e.target.value)} className='w-full border-2 p-2 rounded-lg focus:border-secondary focus:outline-none' />
                </div>
                <div onClick={() => {
                    setFromDate('');
                    setToDate('');
                }} className='py-2 px-3 border border-secondary text-secondary rounded bg-secondary/10 flex items-center justify-center gap-2 cursor-pointer transition-all hover:bg-secondary hover:text-white'>
                    <BiFilterAlt />
                    Reset
                </div>
            </div>
            <div className="">
                <DashboardChartCard
                    pickupChartData={pickupChartData}
                    pickupData={pickupData}
                    deliveryChartData={deliveryChartData}
                    deliveryData={deliveryData}
                />
            </div>
            {(dashboardLoading) && <CustomLoading />}
        </div>
    );
};

export default Dashboard;
